/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';

const requestFlightLegsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedFlightLegsSuccess.create(response);
};

const requestFlightLegsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedFlightLegsError.create(
    error.message !== undefined ? error.message : 'Error requesting tail details',
  );
};

const requestFlightLegsInfoSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedFlightLegsInfoSuccess.create(response);
};

const requestFlightLegsInfoError = (error: AjaxError): any => {
  return ActionCreators.ReceivedFlightLegsInfoError.create(
    error.message !== undefined ? error.message : 'Error requesting flight leg info',
  );
};

const requestFlightLegUploadSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedFlightLegUploadSuccess.create(response);
};

const requestFlightLegUploadError = (error: AjaxError): any => {
  return ActionCreators.ReceivedFlightLegUploadError.create(
    error.message !== undefined ? error.message : 'Error uploading flight leg file',
  );
};

const requestFlightLegOrderSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedFlightLegOrderSuccess.create(response);
};

const requestFlightLegOrderError = (error: AjaxError): any => {
  return ActionCreators.ReceivedFlightLegOrderError.create(
    error.message !== undefined ? error.message : 'Error changing order of flight legs',
  );
};

const requestFlightLegDeleteSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedFlightLegDeleteSuccess.create(response);
};

const requestFlightLegDeleteError = (error: AjaxError): any => {
  return ActionCreators.ReceivedFlightLegDeleteError.create(
    error.message !== undefined ? error.message : 'Error deleting flight leg',
  );
};
const updateTakeoffTimeSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.UpdateTakeoffTimeSuccess.create();
};

const updateTakeoffTimeError = (error: AjaxError): any => {
  return ActionCreators.UpdateTakeoffTimeError.create(
    error.message !== undefined ? error.message : 'Error updating takeoff time',
  );
};
const getFlightLegsInfo = (missionPlanId: number, userId: string): any => {
  return ActionCreators.RequestFlightLegsInfo.create({ missionPlanId, userId });
};

export const getFlightLegsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestFlightLegs.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/mpt/flightlegs/bymissionplanid', $action.payload).pipe(
          map((response: AjaxResponse<any>): Action => requestFlightLegsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(
                error,
                of(requestFlightLegsError(error))
              ),
          ),
        ),
    ),
  );

export const getFlightLegsInfoEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestFlightLegsInfo.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw
          .getWithHeaders('/mpt/flightlegs/infos', {
            missionPlanId: $action.payload.missionPlanId,
            userid: $action.payload.userId,
          })
          .pipe(
            map((response: AjaxResponse<any>): Action => requestFlightLegsInfoSuccess(response)),
            catchError(
              (error: AjaxError): Observable<Action> =>
                handleErrorInterceptor(
                  error,
                  of(requestFlightLegsInfoError(error))
                ),
            ),
          ),
    ),
  );

export const parseFlightLegFileEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestFlightLegUpload.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw
          .postFilesWithHeaders('/mpt/flightlegs/upload', $action.payload.files, {
            missionPlanId: $action.payload.missionPlanId,
            userid: $action.payload.userId,
          })
          .pipe(
            map((response: AjaxResponse<any>): Action => requestFlightLegUploadSuccess(response)),
            catchError(
              (error: AjaxError): Observable<Action> =>
                handleErrorInterceptor(error, of(requestFlightLegUploadError(error))),
            ),
          ),
    ),
  );

export const updateFlightLegsOrderEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestFlightLegOrder.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw
          .postWithHeaders('/mpt/flightlegs/updatelegsorder', $action.payload.idList.join(), {
            missionPlanId: $action.payload.missionPlanId,
            userid: $action.payload.userId,
          })
          .pipe(
            map((response: AjaxResponse<any>): Action => requestFlightLegOrderSuccess(response)),
            catchError(
              (error: AjaxError): Observable<Action> =>
                handleErrorInterceptor(
                  error,
                  of(requestFlightLegOrderError(error))
                ),
            ),
          ),
    ),
  );

export const deleteFlightLegEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestFlightLegDelete.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw
          .deleteWithHeaders(
            `/mpt/flightlegs/${$action.payload.flightLegId}`,
            {},
            {
              userid: $action.payload.userId,
            },
          )
          .pipe(
            map((response: AjaxResponse<any>): Action => requestFlightLegDeleteSuccess(response)),
            catchError(
              (error: AjaxError): Observable<Action> =>
                handleErrorInterceptor(
                  error,
                  of(requestFlightLegDeleteError(error))
                ),
            ),
          ),
    ),
  );

export const updateTakeoffTimeEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.UpdateTakeoffTime.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw
          .putWithHeaders(
            `/mpt/flightlegs/${$action.payload.flightLegId}/takeofftime`,
            $action.payload.takeoffTimeNumberOfMinutes,
            {
              userid: state$.value.session.tokens?.idToken?.claims?.email,
            },
          )
          .pipe(
            mergeMap((response: AjaxResponse<any>): Action[] => [
              updateTakeoffTimeSuccess(response),
              getFlightLegsInfo($action.payload.missionPlanId, state$.value.session.tokens?.idToken?.claims?.email!),
            ]),
            catchError(
              (error: AjaxError): Observable<Action> =>
                handleErrorInterceptor(
                  error,
                  of(updateTakeoffTimeError(error))
                ),
            ),
          ),
    ),
  );
